/**
 * Agnostic authentication token provider interface
 * Allows different authentication systems to be plugged in without changing client code
 */

export interface AuthTokenProvider {
  /**
   * Get the current authentication token
   * @returns Promise that resolves to the token string or null if not authenticated
   */
  getToken(): Promise<string | null>

  /**
   * Check if the user is currently authenticated
   * @returns Promise that resolves to true if authenticated, false otherwise
   */
  isAuthenticated(): Promise<boolean>

  /**
   * Subscribe to authentication state changes
   * @param callback Function to call when auth state changes
   * @returns Unsubscribe function
   */
  onAuthStateChange(callback: (isAuthenticated: boolean) => void): () => void
}

/**
 * Default implementation that uses localStorage
 * This is a fallback for simple token-based auth systems
 */
export class LocalStorageAuthProvider implements AuthTokenProvider {
  private tokenKey: string

  constructor(tokenKey: string = 'auth-token') {
    this.tokenKey = tokenKey
  }

  async getToken(): Promise<string | null> {
    if (typeof window === 'undefined') {
      return null
    }
    return localStorage.getItem(this.tokenKey)
  }

  async isAuthenticated(): Promise<boolean> {
    const token = await this.getToken()
    return token !== null
  }

  onAuthStateChange(callback: (isAuthenticated: boolean) => void): () => void {
    // For localStorage, we can listen to storage events
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === this.tokenKey) {
        callback(e.newValue !== null)
      }
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('storage', handleStorageChange)
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('storage', handleStorageChange)
      }
    }
  }
}

/**
 * Global auth provider instance
 * Can be set by the application to use different auth systems
 */
let globalAuthProvider: AuthTokenProvider | null = null

/**
 * Set the global auth provider
 * This should be called during app initialization
 */
export function setAuthProvider(provider: AuthTokenProvider): void {
  globalAuthProvider = provider
}

/**
 * Get the current auth provider
 * Falls back to localStorage provider if none is set
 */
export function getAuthProvider(): AuthTokenProvider {
  if (!globalAuthProvider) {
    globalAuthProvider = new LocalStorageAuthProvider()
  }
  return globalAuthProvider
}

/**
 * Convenience function to get the current token
 */
export async function getAuthToken(): Promise<string | null> {
  const provider = getAuthProvider()
  return provider.getToken()
}

/**
 * Convenience function to check if user is authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
  const provider = getAuthProvider()
  return provider.isAuthenticated()
}
